import Button from '@app/components/common/Button';
import DatePicker from '@app/components/common/DatePicker';
import Dropdown from '@app/components/common/Dropdown';
import Grid from '@app/components/common/Grid';
import Modal from '@app/components/common/Modal';
import TimePicker from '@app/components/common/TimePicker/TimePicker';
import Typography from '@app/components/common/Typography';
import { dateTimeToSettingsTimeZone } from '@app/helpers/time/time';
import useRootSelector from '@app/hooks/useRootSelector';
import {
  selectCurrentTimeZone,
  selectRoles,
} from '@app/state/selectors/settingsSelectors';
import {
  addEmployeeShiftWeb,
  deleteEmployeeShiftWeb,
  editEmployeeShiftWeb,
} from '@app/state/settings/management/employeesWebActions';
import { useAppDispatch } from '@app/state/store';
import { selectUsers } from '@app/state/users/usersSelectors';
import {
  DateTime,
  rolesDropdownFactory,
  TEditEmployeeShift,
} from '@westondev/tableturn-core';
import { useContext, useMemo } from 'react';
import { WithTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { EmployeesScreenContext } from '../../../EmployeesContext';

interface IAddEditShiftModal extends WithTranslation {
  active: boolean;
  isAdd?: boolean;
  onCancel?: () => void;
  name: string;
}

const AddEditShiftModal = ({
  active,
  isAdd,
  onCancel = () => null,
  t,
  name,
}: IAddEditShiftModal) => {
  const timeZone = useSelector(selectCurrentTimeZone);
  const roles = useSelector(selectRoles);

  const dispatch = useAppDispatch();

  const { shift, setShift, fetchAllDetails } = useContext(
    EmployeesScreenContext,
  );

  const userRoles = useRootSelector(
    state => selectUsers(state)[shift?.organizationUserId || 0]?.roleIds,
  );

  const rolesDropdownOptions = useMemo(
    () =>
      rolesDropdownFactory(roles).filter(roleDropdown =>
        (userRoles || []).includes(roleDropdown.value),
      ),
    [roles, userRoles],
  );

  const handleOnChange = (
    value: number | string,
    field: keyof TEditEmployeeShift,
    isTime = false,
  ) => {
    if (!shift) {
      return;
    }

    if (!['startShift', 'endShift'].includes(field)) {
      return setShift({
        ...shift,
        [field]: value,
      });
    }

    let luxonDate = DateTime.fromISO(value as string).toUTC();

    const currentLuxonDate = DateTime.fromISO(
      shift[field as 'startShift'] || '',
    ).toUTC();

    if (isTime) {
      luxonDate = luxonDate.set({
        year: currentLuxonDate.year,
        month: currentLuxonDate.month,
        day: currentLuxonDate.day,
      });
      const offsetMinutes = luxonDate.setZone(timeZone).offset;

      luxonDate = luxonDate.plus({ minutes: -offsetMinutes });
    }

    let newDate = '';

    if (isTime) {
      newDate = currentLuxonDate
        .toUTC()
        .set({ minute: luxonDate.minute, hour: luxonDate.hour })
        .toISO();
    } else {
      newDate = currentLuxonDate.isValid
        ? luxonDate
            .set({
              minute: currentLuxonDate.minute,
              hour: currentLuxonDate.hour,
            })
            .toISO()
        : luxonDate.toISO();
    }

    setShift({
      ...shift,
      [field]: newDate,
    });
  };

  const isSaveDisabled = useMemo(() => {
    if (!shift) {
      return true;
    }

    if (!shift.roleId || !shift.startShift || !shift.endShift) {
      return true;
    }

    const luxonStartShift = DateTime.fromISO(shift.startShift);
    const luxonEndShift = DateTime.fromISO(shift.endShift);

    if (luxonEndShift <= luxonStartShift) {
      return true;
    }

    return false;
  }, [shift]);

  const handleSaveChanges = async () => {
    let success = true;

    if (!shift) {
      return;
    }
    onCancel();

    if (isAdd) {
      success = await dispatch(addEmployeeShiftWeb(shift));
    } else {
      success = await dispatch(editEmployeeShiftWeb(shift));
    }

    if (success) {
      await fetchAllDetails();
    }
  };

  const handleDelete = async () => {
    if (!shift) {
      return;
    }
    onCancel();
    const success = await dispatch(deleteEmployeeShiftWeb(shift));

    if (success) {
      await fetchAllDetails();
    }
  };

  const startShiftTime = useMemo(
    () =>
      shift?.startShift
        ? dateTimeToSettingsTimeZone(shift?.startShift).toFormat('HH:mm')
        : '',
    [shift?.startShift],
  );

  const endShiftTime = useMemo(
    () =>
      shift?.endShift
        ? dateTimeToSettingsTimeZone(shift?.endShift).toFormat('HH:mm')
        : '',
    [shift?.endShift],
  );

  const startShiftMaxValue = useMemo(() => {
    const startShiftLuxon = DateTime.fromISO(shift?.startShift || '');
    const endShiftLuxon = DateTime.fromISO(shift?.endShift || '');
    const todayLuxon = DateTime.now();
    if (
      startShiftLuxon.isValid &&
      endShiftLuxon.isValid &&
      startShiftLuxon.hasSame(endShiftLuxon, 'day')
    ) {
      return endShiftLuxon.toFormat('HH:mm');
    }

    if (startShiftLuxon.hasSame(todayLuxon, 'day')) {
      return todayLuxon.toFormat('HH:mm');
    }

    return undefined;
  }, [shift?.endShift, shift?.startShift]);

  const endShiftMinValue = useMemo(() => {
    const startShiftLuxon = DateTime.fromISO(shift?.startShift || '');
    const endShiftLuxon = DateTime.fromISO(shift?.endShift || '');
    if (
      startShiftLuxon.isValid &&
      endShiftLuxon.isValid &&
      startShiftLuxon.hasSame(endShiftLuxon, 'day')
    ) {
      return startShiftLuxon.toFormat('HH:mm');
    }

    return undefined;
  }, [shift?.endShift, shift?.startShift]);

  const endShiftMaxValue = useMemo(() => {
    const endShiftLuxon = DateTime.fromISO(shift?.endShift || '');
    const todayLuxon = DateTime.now();

    if (endShiftLuxon.hasSame(todayLuxon, 'day')) {
      return todayLuxon.toFormat('HH:mm');
    }

    return undefined;
  }, [shift?.endShift]);

  return (
    <Modal
      size={600}
      isActive={active}
      title={
        isAdd
          ? t('managementModule.employeesScreen.employeeDetailsTable.addShift')
          : t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.title',
            )
      }
      subtitle={name}
      onModalClose={onCancel}
      footer={
        <>
          <Button
            variant="secondary"
            csx={{
              marginRight: 5,
            }}
            onClick={onCancel}>
            {t('commonButtons.cancel')}
          </Button>
          {!isAdd && (
            <Button
              variant="danger"
              csx={{
                marginRight: 5,
              }}
              onClick={handleDelete}>
              {t('commonButtons.delete')}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={handleSaveChanges}
            disabled={isSaveDisabled}>
            {t('commonButtons.saveChanges')}
          </Button>
        </>
      }>
      <Grid columnGap={20} rowGap={10}>
        <Grid.Item>
          <Dropdown
            required
            label={t('menuScreen.common.settings.roles.singular')}
            placeholder={t('commonTexts.placeholderDropdown')}
            data={[rolesDropdownOptions]}
            onChange={newRoleId => handleOnChange(newRoleId, 'roleId')}
            value={shift?.roleId || 0}
          />
        </Grid.Item>
        <Grid.Item>
          <Typography color="lightBlue">
            {t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.startOfShift',
            )}
          </Typography>
        </Grid.Item>
        <Grid.Item mb={6}>
          <DatePicker
            label={t('commonTexts.date')}
            required
            value={shift?.startShift || ''}
            maxValue={shift?.endShift || DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'startShift')}
          />
        </Grid.Item>
        <Grid.Item mb={6}>
          <TimePicker
            label={t('commonTexts.time')}
            required
            utcValue={startShiftTime || ''}
            maxValue={startShiftMaxValue}
            onChange={date => handleOnChange(date, 'startShift', true)}
          />
        </Grid.Item>
        <Grid.Item>
          <Typography color="lightBlue">
            {t(
              'managementModule.employeesScreen.employeeDetailsTable.editShiftModal.endOfShift',
            )}
          </Typography>
        </Grid.Item>
        <Grid.Item mb={6}>
          <DatePicker
            label={t('commonTexts.date')}
            required
            value={shift?.endShift || ''}
            minValue={shift?.startShift}
            maxValue={DateTime.now().toISODate()}
            onChange={date => handleOnChange(date, 'endShift')}
          />
        </Grid.Item>
        <Grid.Item mb={6}>
          <TimePicker
            label={t('commonTexts.time')}
            required
            utcValue={endShiftTime || ''}
            minValue={endShiftMinValue}
            maxValue={endShiftMaxValue}
            onChange={date => handleOnChange(date, 'endShift', true)}
          />
        </Grid.Item>
      </Grid>
    </Modal>
  );
};

export default AddEditShiftModal;
